<template>
  <div class="filters">
    <filter-row :rowFilters="rowFilters" :filters="filters"></filter-row>
    <filter-box
      v-if="filters"
      :rowFilters="rowFilters"
      :locationTotalCount="locationTotalCount"
      class="show-on-mobile"
      :filters="filters"
    ></filter-box>
  </div>
</template>

<script setup lang="ts">
import useSafeRoute from '@/app/composables/useSafeRoute';
import FilterBox from './filter-box.vue';
import FilterRow from './filter-row.vue';
import useEventBus from '@/app/composables/useEventBus';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import type { FiltersType } from '@/schemas/filtersSchema';
import type { RowFilterType } from './filterTypes';

withDefaults(
  defineProps<{
    locationTotalCount?: number;
    filters?: FiltersType;
  }>(),
  {
    locationTotalCount: 0,
    filters: () => ({}),
  }
);

const route = useSafeRoute();
const bus = useEventBus();
const defaultFilters: RowFilterType = {
  featureGroup: true,
  location: true,
  priceMin: true,
  livingAreaMin: true,
  siteAreaMin: true,
  totalAreaMin: true,
  rentMin: true,
  numberOfRooms: true,
  condition: false,
  listingTypes: true,
  extraFeatures: true,
  yearMin: false,
  propertyId: false,
  showings: false,
  siteOwnership: false,
  energy: false,
};
const rowFilters = ref<RowFilterType>({ ...defaultFilters });

const visibleFilters = (overrides: Partial<RowFilterType>): RowFilterType => {
  return { ...defaultFilters, ...overrides };
};

const breakPoints: { breakpointMax: number; filters: RowFilterType }[] = [
  { breakpointMax: 1680, filters: visibleFilters({}) },
  { breakpointMax: 1400, filters: visibleFilters({ extraFeatures: false }) },
  { breakpointMax: 1300, filters: visibleFilters({ extraFeatures: false }) },
  {
    breakpointMax: 1023,
    filters: visibleFilters({ numberOfRooms: false, listingTypes: false, extraFeatures: false }),
  },
  {
    breakpointMax: 0,
    filters: visibleFilters({
      featureGroup: false,
      location: false,
      priceMin: false,
      livingAreaMin: false,
      siteAreaMin: false,
      totalAreaMin: false,
      rentMin: false,
      numberOfRooms: false,
      condition: false,
      listingTypes: false,
      extraFeatures: false,
      yearMin: false,
      propertyId: false,
      showings: false,
      siteOwnership: false,
      energy: false,
    }),
  },
];

const setFilterVisibilities = (): void => {
  const windowWidth: number = window.innerWidth;
  const correctBreakpoint: { breakpointMax: number; filters: RowFilterType } | undefined = breakPoints.find(
    (point) => windowWidth > point.breakpointMax || point.breakpointMax === 0
  );
  if (correctBreakpoint) {
    rowFilters.value = correctBreakpoint.filters;
  }
};

watch(
  () => route.value.path,
  (to, from) => {
    bus.$off(`windowResized-${from}`, null);
    bus.$on(`windowResized-${to}`, setFilterVisibilities);
  }
);

onMounted(() => {
  setFilterVisibilities();
  bus.$on(`windowResized-${route.value.path}`, setFilterVisibilities);
});
onBeforeUnmount(() => {
  bus.$off(`windowResized-${route.value.path}`, null);
});
</script>
